import React, { useState, useEffect, useCallback } from 'react';
import { usersAPI, authAPI } from '../../services/api';
import { Table } from '../../components/common/Table';
import { Button } from '../../components/common/Button';
import { Modal } from '../../components/common/Modal';
import { Badge } from '../../components/common/Badge';
import { Pagination } from '../../components/common/Pagination';
import { PageHeader } from '../../components/layout/PageHeader';
import { FormField, Input, Select } from '../../components/common/FormField';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { format } from 'date-fns';

const EMPTY = { name:'', email:'', password:'', role:'employee', phone:'', department:'' };

export function UsersPage() {
  const [users, setUsers]         = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [role, setRole]           = useState('');
  const [page, setPage]           = useState(1);
  const [open, setOpen]           = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]       = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await usersAPI.getAll({ search, role, page, limit:10 });
      setUsers(r.data||[]); setPagination(r.pagination);
    } catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, [search, role, page]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit   = u  => { setEditing(u);    setForm({ name:u.name, email:u.email, password:'', role:u.role, phone:u.phone||'', department:u.department||'' }); setOpen(true); };

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { const d={...form}; if(!d.password) delete d.password; await usersAPI.update(editing._id, d); toast.success('Updated'); }
      else         { await authAPI.register(form); toast.success('Created'); }
      setOpen(false); load();
    } catch (err) { toast.error(err.message||'Failed'); }
    finally { setSaving(false); }
  };

  const del = async id => {
    if(!window.confirm('Deactivate user?')) return;
    try { await usersAPI.delete(id); toast.success('Deactivated'); load(); }
    catch { toast.error('Failed'); }
  };

  const s = (k,v) => setForm(f=>({...f,[k]:v}));

  const cols = [
    { header:'Name', render:u=>(
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center"><span className="text-indigo-600 font-bold text-xs">{(u.name||'?')[0].toUpperCase()}</span></div>
        <div><p className="font-medium text-gray-900 text-sm">{u.name}</p><p className="text-xs text-gray-400">{u.email}</p></div>
      </div>
    )},
    { header:'Role',       render:u=><Badge status={u.role}/> },
    { header:'Department', render:u=><span className="text-sm text-gray-600">{u.department||'—'}</span> },
    { header:'Status',     render:u=><Badge status={u.isActive?'active':'inactive'}/> },
    { header:'Joined',     render:u=><span className="text-xs text-gray-400">{format(new Date(u.createdAt),'dd MMM yyyy')}</span> },
    { header:'', render:u=>(
      <div className="flex gap-1">
        <button onClick={()=>openEdit(u)}  className="p-1.5 hover:bg-indigo-50 rounded-lg"><PencilIcon className="w-4 h-4 text-indigo-600"/></button>
        <button onClick={()=>del(u._id)}   className="p-1.5 hover:bg-red-50 rounded-lg"><TrashIcon className="w-4 h-4 text-red-500"/></button>
      </div>
    )},
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Users" subtitle="Manage employees and accountants" actions={<Button icon={PlusIcon} onClick={openCreate}>Add User</Button>}/>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex gap-3 flex-wrap">
          <div className="relative flex-1 min-w-44">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"/>
            <input value={search} onChange={e=>{setSearch(e.target.value);setPage(1);}} placeholder="Search users..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"/>
          </div>
          <select value={role} onChange={e=>{setRole(e.target.value);setPage(1);}} className="px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white">
            <option value="">All Roles</option>
            <option value="admin">Admin</option>
            <option value="employee">Employee</option>
            <option value="accountant">Accountant</option>
          </select>
        </div>
        <Table columns={cols} data={users} loading={loading}/>
        <Pagination pagination={pagination} onPageChange={setPage}/>
      </div>
      <Modal isOpen={open} onClose={()=>setOpen(false)} title={editing?'Edit User':'New User'}>
        <form onSubmit={save} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Name" required><Input value={form.name} onChange={e=>s('name',e.target.value)} required placeholder="Full name"/></FormField>
            <FormField label="Email" required><Input type="email" value={form.email} onChange={e=>s('email',e.target.value)} required placeholder="Email"/></FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Role" required><Select value={form.role} onChange={e=>s('role',e.target.value)}><option value="employee">Employee</option><option value="accountant">Accountant</option><option value="admin">Admin</option></Select></FormField>
            <FormField label="Password" required={!editing}><Input type="password" value={form.password} onChange={e=>s('password',e.target.value)} placeholder={editing?'Leave blank to keep':'Min 6 chars'} required={!editing} minLength={editing?0:6}/></FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Phone"><Input value={form.phone} onChange={e=>s('phone',e.target.value)} placeholder="+91 98765 43210"/></FormField>
            <FormField label="Department"><Input value={form.department} onChange={e=>s('department',e.target.value)} placeholder="Engineering"/></FormField>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" loading={saving} className="flex-1">{editing?'Update':'Create'} User</Button>
            <Button type="button" variant="ghost" onClick={()=>setOpen(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}